<template>
  <van-popup
    v-model:show="show"
    round
    closeable
    position="bottom"
    class="role-popup-redesign"
    :style="{ height: '78%' }"
  >
    <div class="role-popup-redesign__shell">
      <header class="role-popup-redesign__header">
        <h3 class="role-popup-redesign__title">
          {{ $t("app.selectrolepopup.changerole") }}
        </h3>
        <p v-if="totalRoleCount" class="role-popup-redesign__meta">
          {{
            $t("app.selectrolepopup.totalroles", { count: totalRoleCount })
          }}
        </p>
      </header>

      <div class="role-popup-redesign__search">
        <van-search
          v-model="searchQuery"
          :placeholder="$t('app.selectrolepopup.searchplaceholder')"
          shape="round"
          clearable
          show-action
          @clear="onSearchClear"
        >
          <template #action>
            <button
              v-if="searchQuery.trim()"
              type="button"
              class="role-popup-redesign__search-clear"
              @click="onSearchClear"
            >
              {{ $t("app.selectrolepopup.clearsearch") }}
            </button>
          </template>
        </van-search>
      </div>

      <div class="role-popup-redesign__body">
        <template v-if="isSearching">
          <div v-if="searchResults.length" class="role-popup-redesign__list">
            <button
              v-for="(item, index) in searchResults"
              :key="`${item.id}-${index}`"
              type="button"
              class="role-popup-redesign__result"
              @click="handleItemClick(item)"
            >
              <span class="role-popup-redesign__result-body">
                <span class="role-popup-redesign__result-type">
                  {{ item.maintitle }}
                </span>
                <span class="role-popup-redesign__result-name">
                  {{ item.fullName }}
                </span>
              </span>
              <van-icon
                name="arrow"
                class="role-popup-redesign__result-arrow"
                aria-hidden="true"
              />
            </button>
          </div>

          <div v-else class="role-popup-redesign__empty">
            <van-icon name="search" class="role-popup-redesign__empty-icon" />
            <p class="role-popup-redesign__empty-title">
              {{ $t("app.selectrolepopup.noresults") }}
            </p>
            <p class="role-popup-redesign__empty-desc">
              {{ $t("app.selectrolepopup.noresultsdesc") }}
            </p>
          </div>
        </template>

        <div v-else class="role-popup-redesign__list">
          <SelectDownItemRedesign
            v-for="(item, index) in roleList"
            :key="index"
            class="role-popup-redesign__group"
            :roleItem="item"
            @handleItemClick="handleItemClick"
          />
        </div>
      </div>

      <footer class="role-popup-redesign__footer">
        <button
          type="button"
          class="role-popup-redesign__create"
          @click="onCreateRole"
        >
          {{ $t("app.selectrolepopup.createnewrole") }}
        </button>
      </footer>
    </div>
  </van-popup>
</template>

<script>
import { computed, ref, toRefs, watch } from "vue";
import SelectDownItemRedesign from "@/components/SelectDownItemRedesign.vue";
import { useRouter } from "vue-router";

export const useRolePopupEffect = () => {
  const showAccountPopupFlag = ref(false);
  return { showAccountPopupFlag };
};

export default {
  name: "SelectRolePopupRedesign",
  components: {
    SelectDownItemRedesign,
  },
  emits: ["goOrder"],
  props: {
    roleList: {
      type: Array,
      default: () => [],
    },
    showAccountPopupFlag: {
      type: Boolean,
    },
  },

  setup(props, { emit }) {
    const router = useRouter();
    const { showAccountPopupFlag, roleList } = toRefs(props);
    const searchQuery = ref("");

    const show = computed({
      get: () => showAccountPopupFlag.value,
      set: (val) => emit("update:showAccountPopupFlag", val),
    });

    const allRoles = computed(() =>
      roleList.value.flatMap((group) => group || [])
    );

    const totalRoleCount = computed(() => allRoles.value.length);

    const isSearching = computed(() => searchQuery.value.trim().length > 0);

    const searchResults = computed(() => {
      const query = searchQuery.value.trim().toLowerCase();
      if (!query) {
        return [];
      }
      return allRoles.value.filter((item) => {
        const haystack = `${item.maintitle || ""} ${item.fullName || ""}`
          .toLowerCase()
          .trim();
        return haystack.includes(query);
      });
    });

    const handleItemClick = (id, item) => {
      emit("changeRole", id, item);
    };

    const onCreateRole = () => {
      emit("update:showAccountPopupFlag", false);
      router.push({ name: "RoleSelect" });
    };

    const onSearchClear = () => {
      searchQuery.value = "";
    };

    watch(show, (visible) => {
      if (!visible) {
        searchQuery.value = "";
      }
    });

    return {
      show,
      searchQuery,
      totalRoleCount,
      isSearching,
      searchResults,
      handleItemClick,
      roleList,
      onCreateRole,
      onSearchClear,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/style/mixins.scss";

$green: #009343;
$green-deep: #007a3a;
$green-light: #e8f5ed;
$page-bg: #f4f7f5;
$surface: #ffffff;
$ink: #1a1f1c;
$label: #8a9490;
$border: #e8edea;
$card-radius: 0.12rem;
$btn-radius: 0.16rem;
$ease-out: cubic-bezier(0.22, 1, 0.36, 1);

.role-popup-redesign {
  text-align: left;

  &__shell {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    background: $page-bg;
  }

  &__header {
    flex-shrink: 0;
    padding: 0.18rem 0.16rem 0.08rem;
    background: linear-gradient(180deg, $green 0%, $green-deep 100%);
    color: #ffffff;
  }

  &__title {
    margin: 0;
    font-size: 0.17rem;
    font-weight: 700;
    line-height: 1.3;
    text-align: center;
  }

  &__meta {
    margin: 0.06rem 0 0;
    font-size: 0.11rem;
    font-weight: 500;
    line-height: 1.4;
    text-align: center;
    color: rgba(255, 255, 255, 0.86);
  }

  &__search {
    flex-shrink: 0;
    padding: 0.12rem 0.16rem;
    background: $surface;
    border-bottom: 0.01rem solid $border;

    :deep(.van-search) {
      padding: 0;
      background: transparent;
    }

    :deep(.van-search__content) {
      background: $page-bg;
      border: 0.01rem solid $border;
    }

    :deep(.van-field__control) {
      color: $ink;
    }

    :deep(.van-field__control::placeholder) {
      color: $label;
    }
  }

  &__search-clear {
    padding: 0;
    border: none;
    background: transparent;
    color: $green;
    font-size: 0.12rem;
    font-weight: 600;
    line-height: 1.4;
    cursor: pointer;
  }

  &__body {
    flex: 1;
    min-height: 0;
    @include smooth-scroll;
    padding: 0.12rem 0.16rem;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 0.12rem;
  }

  &__group {
    width: 100%;
    text-align: left;
  }

  &__result {
    display: flex;
    align-items: center;
    gap: 0.12rem;
    width: 100%;
    padding: 0.14rem;
    border: 0.01rem solid $border;
    border-radius: $card-radius;
    background: $surface;
    text-align: left;
    cursor: pointer;
    box-shadow: 0 0.02rem 0.08rem rgba(13, 40, 24, 0.04);
    transition: background 0.15s $ease-out, border-color 0.15s $ease-out;

    &:active {
      background: $green-light;
      border-color: rgba(0, 147, 67, 0.22);
    }
  }

  &__result-body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.04rem;
  }

  &__result-type {
    font-size: 0.11rem;
    font-weight: 600;
    color: $green;
    line-height: 1.35;
  }

  &__result-name {
    font-size: 0.14rem;
    font-weight: 600;
    color: $ink;
    line-height: 1.4;
    overflow-wrap: anywhere;
  }

  &__result-arrow {
    flex-shrink: 0;
    color: $green;
    font-size: 0.14rem;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 1.8rem;
    padding: 0.24rem 0.16rem;
    text-align: center;
  }

  &__empty-icon {
    font-size: 0.36rem;
    color: $label;
  }

  &__empty-title {
    margin: 0.12rem 0 0;
    font-size: 0.14rem;
    font-weight: 600;
    color: $ink;
    line-height: 1.4;
  }

  &__empty-desc {
    margin: 0.06rem 0 0;
    font-size: 0.12rem;
    color: $label;
    line-height: 1.45;
  }

  &__footer {
    flex-shrink: 0;
    padding: 0.12rem 0.16rem calc(0.12rem + env(safe-area-inset-bottom, 0px));
    background: $surface;
    border-top: 0.01rem solid $border;
    box-shadow: 0 -0.02rem 0.1rem rgba(0, 0, 0, 0.04);
  }

  &__create {
    width: 100%;
    min-height: 0.44rem;
    padding: 0.12rem 0.16rem;
    border: none;
    border-radius: $btn-radius;
    background: linear-gradient(90deg, #026b32 0%, $green 100%);
    color: #ffffff;
    font-size: 0.14rem;
    font-weight: 700;
    line-height: 1.3;
    cursor: pointer;
    transition: opacity 0.15s $ease-out;

    &:active {
      opacity: 0.92;
    }
  }
}

:deep(.van-popup__close-icon) {
  color: #ffffff;
  top: 0.16rem;
}

@media (prefers-reduced-motion: reduce) {
  .role-popup-redesign__result,
  .role-popup-redesign__create {
    transition: none;
  }
}
</style>
